import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, ListChecks } from "lucide-react";
import { ticketPrice, gameSummary, type LotterySlug } from "@/lib/pool-math";
import { cn, formatBRL, formatInt } from "@/lib/utils";

export type GameSource = "manual" | "complete" | "closure" | "pairs" | "fixed" | "smart" | "ai";

export const SOURCE_LABEL: Record<GameSource, string> = {
  manual: "Montado por você",
  complete: "Combinações completas",
  closure: "Fechamento estratégico",
  pairs: "Por duplas",
  fixed: "Fixas + variáveis",
  smart: "Gerador inteligente",
  ai: "Gerador IA",
};

export interface BasketGame {
  numbers: number[];
  source: GameSource;
  /** Dezena âncora em jogos gerados por duplas com seleção ímpar. */
  anchor?: number;
}

export const gameKey = (numbers: number[]) =>
  [...numbers].sort((a, b) => a - b).join("-");

export function basketCost(slug: LotterySlug, games: BasketGame[]) {
  return games.reduce((acc, g) => acc + ticketPrice(slug, g.numbers.length), 0);
}

/**
 * Cesta de jogos do bolão. Aceita jogos de origens diferentes (manual,
 * combinações, gerador inteligente) e tamanhos diferentes de dezenas.
 * Bloqueia duplicados e persiste por loteria.
 */
export function usePoolBasket(slug: LotterySlug, storageKey = "lt-bolao-basket") {
  const key = `${storageKey}-${slug}`;
  const [games, setGames] = useState<BasketGame[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      setGames(
        Array.isArray(parsed)
          ? parsed.filter((g: BasketGame) => Array.isArray(g?.numbers) && g.numbers.length > 0)
          : [],
      );
    } catch {
      setGames([]);
    }
  }, [key]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(key, JSON.stringify(games));
    } catch { /* ignore */ }
  }, [key, games]);

  /** Retorna quantos entraram e quantos foram ignorados por duplicidade. */
  const add = useCallback((incoming: number[][], source: GameSource, anchor?: number) => {
    let added = 0;
    let duplicates = 0;
    setGames((prev) => {
      const seen = new Set(prev.map((g) => gameKey(g.numbers)));
      const next = [...prev];
      for (const numbers of incoming) {
        const sorted = [...numbers].sort((a, b) => a - b);
        const k = gameKey(sorted);
        if (seen.has(k)) { duplicates++; continue; }
        seen.add(k);
        next.push({ numbers: sorted, source, anchor });
        added++;
      }
      return next;
    });
    return { added, duplicates };
  }, []);

  const removeAt = useCallback((index: number) => {
    setGames((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clear = useCallback(() => {
    setGames([]);
    if (typeof window !== "undefined") {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
  }, [key]);

  return { games, add, removeAt, clear, setGames };
}

interface Props {
  slug: LotterySlug;
  games: BasketGame[];
  onRemove: (index: number) => void;
  onClear: () => void;
  footer?: React.ReactNode;
  title?: string;
}

export function PoolGameBasket({ slug, games, onRemove, onClear, footer, title = "Jogos do bolão" }: Props) {
  const cost = basketCost(slug, games);
  return (
    <Card className={cn(games.length > 0 && "border-primary/40")}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-primary" />
            {title}{" "}
            <span className="text-sm font-normal text-muted-foreground">
              ({formatInt(games.length)})
            </span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={games.length === 0}
            className="gap-1.5 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" /> Limpar lista
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {games.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nenhum jogo na lista ainda. Monte seu jogo na grade, gere combinações
            ou use o gerador inteligente e clique em <strong>Adicionar ao bolão</strong>.
          </p>
        ) : (
          <div className="max-h-[420px] space-y-1 overflow-y-auto">
            {games.map((g, i) => {
              const s = gameSummary(g.numbers);
              return (
                <div
                  key={`${gameKey(g.numbers)}-${i}`}
                  className="flex flex-wrap items-center gap-3 rounded-md border border-lt-border bg-lt-bg2 p-2.5 text-[13px]"
                >
                  <span className="w-12 text-xs text-muted-foreground">
                    #{String(i + 1).padStart(3, "0")}
                  </span>
                  <div className="flex flex-1 flex-wrap gap-1">
                    {g.numbers.map((n) => {
                      const isAnchor = n === g.anchor;
                      return (
                        <span
                          key={n}
                          title={isAnchor ? "Dezena âncora (entra em todos os jogos)" : undefined}
                          className={cn(
                            "inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-medium ring-2 ring-offset-1 ring-offset-background",
                            isAnchor
                              ? "border border-lt-gold-border bg-lt-gold text-background ring-lt-gold"
                              : "bg-primary/10 text-primary ring-primary",
                          )}
                        >
                          {String(n).padStart(2, "0")}
                        </span>
                      );
                    })}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Σ {s.sum} • {s.evens}P/{s.odds}I
                  </span>
                  <span className="rounded-full bg-lt-teal/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-lt-teal">
                    {SOURCE_LABEL[g.source]}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {g.numbers.length} dez • {formatBRL(ticketPrice(slug, g.numbers.length))}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => onRemove(i)}
                    title="Remover este jogo"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-lt-gold-border bg-lt-gold-bg p-3 text-[13px]">
          <div>
            <div className="text-muted-foreground">Total de jogos</div>
            <div className="text-lg font-medium">{formatInt(games.length)}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Custo total</div>
            <div className="text-lg font-medium">{formatBRL(cost)}</div>
          </div>
          {footer}
        </div>
      </CardContent>
    </Card>
  );
}
